import { forwardRef, useState, Children, useCallback, useEffect } from 'react';
import useEmblaCarousel from 'embla-carousel-react';
import '../../constants/culture.js';
import { jsxs, Fragment, jsx } from 'react/jsx-runtime';
import { useMediaQuery } from '../../hooks/useMediaQuery.js';
import { useTheme } from 'styled-components';
import 'react-error-boundary';
import '../../theme/globalStyles.js';
import '../../theme/base/index.js';
import '../../theme/baseFit/index.js';
import '../../theme/pureGym/index.js';
import '../../theme/fitnessWorld/index.js';
import { Arrow } from '../arrow/index.js';
import '../button/index.js';
import { CarouselWrapper, CarouselButtonPrev, CarouselButtonNext, CarouselDots, CarouselDot } from './styles.js';
import { BUTTON_VARIANTS } from '../button/constants.js';

const Carousel = /*#__PURE__*/forwardRef(function Carousel(_ref, ref) {
  let {
    children,
    direction = 'ltr',
    draggable = true,
    labels,
    loop = false,
    showDots = false,
    startIndex = 0,
    visibleSlides = 1,
    visibleSlidesDesktop = 3,
    visibleSlidesTablet = 3,
    ...rest
  } = _ref;
  const {
    mediaQueries
  } = useTheme();
  const isMobile = useMediaQuery(mediaQueries.smMax);
  const isTablet = useMediaQuery(mediaQueries.mdMax);
  const isRtl = direction === 'rtl';

  /** Initialise carousel */
  const [viewportRef, embla] = useEmblaCarousel({
    containScroll: 'trimSnaps',
    direction,
    draggable,
    loop,
    startIndex,
    slidesToScroll: isMobile ? visibleSlides : isTablet ? visibleSlidesTablet : visibleSlidesDesktop
  });
  const [prevBtnEnabled, setPrevBtnEnabled] = useState(false);
  const [nextBtnEnabled, setNextBtnEnabled] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [scrollSnaps, setScrollSnaps] = useState([]);
  const arrayChildren = Children.toArray(children);
  const scrollPrev = useCallback(() => embla && embla.scrollPrev(), [embla]);
  const scrollNext = useCallback(() => embla && embla.scrollNext(), [embla]);
  const scrollTo = useCallback(i => embla && embla.scrollTo(i), [embla]);
  const emblaEventHandler = useCallback(() => {
    if (!embla) return;
    setSelectedIndex(embla.selectedScrollSnap());
    setPrevBtnEnabled(embla.canScrollPrev());
    setNextBtnEnabled(embla.canScrollNext());
  }, [embla]);
  useEffect(() => {
    if (!embla) return;
    emblaEventHandler();
    setScrollSnaps(embla.scrollSnapList());
    embla.on('slidesInView', emblaEventHandler);
    embla.on('select', emblaEventHandler);
  }, [embla, emblaEventHandler]);
  return /*#__PURE__*/jsxs(Fragment, {
    children: [/*#__PURE__*/jsxs(CarouselWrapper, {
      dir: direction,
      startIndex: startIndex,
      visibleSlides: visibleSlides,
      visibleSlidesDesktop: visibleSlidesDesktop,
      visibleSlidesTablet: visibleSlidesTablet,
      ref: ref,
      ...rest,
      children: [/*#__PURE__*/jsx("div", {
        className: "carousel__viewport",
        ref: viewportRef,
        children: /*#__PURE__*/jsx("div", {
          className: "carousel__container",
          children: Children.map(arrayChildren, (child, i) => /*#__PURE__*/jsx("div", {
            className: "carousel__slide",
            children: child
          }, "slide-".concat(i)))
        })
      }), /*#__PURE__*/jsx(CarouselButtonPrev, {
        disabled: !prevBtnEnabled,
        onClick: scrollPrev,
        children: /*#__PURE__*/jsx(Arrow, {
          arrowDirection: "backward",
          isRtl: isRtl,
          title: labels.previous
        })
      }), /*#__PURE__*/jsx(CarouselButtonNext, {
        disabled: !nextBtnEnabled,
        onClick: scrollNext,
        children: /*#__PURE__*/jsx(Arrow, {
          arrowDirection: "forward",
          isRtl: isRtl,
          title: labels.next
        })
      })]
    }), showDots && /*#__PURE__*/jsx(CarouselDots, {
      dir: direction,
      children: scrollSnaps.map((_, i) => /*#__PURE__*/jsx(CarouselDot, {
        selected: i === selectedIndex,
        onClick: () => scrollTo(i),
        variant: BUTTON_VARIANTS.PRIMARY
      }, "slideDot-".concat(i)))
    })]
  });
});

export { Carousel };
